import dayjs from 'dayjs';
import { DimensionConfigType, PlaybookKpiConfigType, PromoLibraryKpiConfigType } from 'types/tradePromoOptimization';
import {
    compareUsers,
    formatNumWithKMBAlt,
    formatNumbers,
    formatString,
    getAuthUser,
    getPercentageChangeV2,
    getPercentageChangeV2Decimal,
} from 'utils/helperMethods';
import { CheckCircleFilled } from '@ant-design/icons';
import { colorConstants } from 'config/colorConstants';
import _ from 'lodash';

export function getAllPlansParser(data: any[], dimensionConfigData: DimensionConfigType | null, configData: any) {
    const dim1Attribute = dimensionConfigData?.dim1Attribute;
    const dim2Attribute = dimensionConfigData?.dim2Attribute;
    const planVersionDropdownOptions = configData?.planVersionDropdownOptions;
    try {
        const result = data?.map((item: any) => {
            //only add the dimension object if its available in config.
            let dimensionObject = {};
            if (dim1Attribute) {
                dimensionObject = {
                    [dim1Attribute?.['key']]: formatString(item?.dim1_attribute_value?.join(', ') || '', 15),
                };
            }
            if (dim2Attribute) {
                dimensionObject = {
                    ...dimensionObject,
                    [dim2Attribute?.['key']]: formatString(item?.dim2_attribute_value || '', 15),
                };
            }
            const updatedDataLabel =
                planVersionDropdownOptions?.find(
                    (dropdownValues: any) => dropdownValues?.value === item?.draft_plan_version,
                )?.label ?? '';

            return {
                ...item,
                draft_plan_version_label: updatedDataLabel,
                ...dimensionObject,
                key: item?.id,
                status_icon: <CheckCircleFilled style={{ fontSize: 16, color: colorConstants.limeGreen }} />,
                avatar_short_name: getAuthUser(item?.created_by),
                is_tpo_synced: Boolean(item?.is_tpm_plan),
                is_view_mode: !compareUsers(item?.created_by),
                updated_at: dayjs.unix(item?.updated_at).fromNow(),
                created_at: dayjs.unix(item?.created_at).fromNow(),
                tpm_last_sync_date: item?.tpm_last_sync_date ? dayjs.unix(item?.tpm_last_sync_date).fromNow() : '-',
            };
        });
        return result;
    } catch (error) {
        throw error;
    }
}

export function playbookTableDataParser(
    data: any[],
    config: PlaybookKpiConfigType | null,
    playbookAttributeTypes: any,
    kpis: any[],
    appConfig: any,
) {
    try {
        if (!config) return null;
        const result: any[] = [];
        data?.forEach((item: any) => {
            const tempObj: any = {
                [playbookAttributeTypes?.[0]?.['label']]: item?.dim2_attribute_name,
                key: item?.row_id,
            };
            kpis?.forEach((kpi: any) => {
                tempObj[config[kpi?.key]?.label] = {
                    values: item[kpi?.key],
                    guidelines: formatPlaybookNumbers(
                        item[kpi?.key],
                        config[kpi?.key]?.pre,
                        config[kpi?.key]?.post,
                        appConfig,
                    ),
                };
            });
            result.push(tempObj);
        });
        return result;
    } catch (error) {
        throw error;
    }
}

export function promoLibraryTableDataParser(
    data: any[],
    config: PromoLibraryKpiConfigType | null,
    promoLibraryAttributeTypes: any[],
    kpis: any[],
    appConfig: any,
) {
    try {
        if (!config) return null;
        const result: any[] = [];
        data?.forEach((item: any) => {
            const tempObj: any = {
                key: item?.row_id,
                rowId: item?.row_id,
            };
            promoLibraryAttributeTypes?.forEach((attribute: any) => {
                tempObj[attribute?.label] = item?.[`${attribute?.dimension}_attribute_name`];
            });

            kpis?.forEach((kpiItem: any) => {
                const kpi = kpiItem?.key;
                let unformattedOriginalValue = null;
                let unformattedNewValue = null;
                let originalValue = null;
                let newValue = null;
                if (['text', 'boolean'].includes(config[kpi]?.type)) {
                    unformattedOriginalValue = item[kpi]?.old;
                    unformattedNewValue = item[kpi]?.new;
                    originalValue = item[kpi]?.old;
                    newValue = item[kpi]?.new;
                } else {
                    (unformattedOriginalValue = item[kpi]?.old?.map((numberItem: number) => {
                        return _.isNil(numberItem) ? numberItem : _.round(numberItem, config[kpi]?.allowedDecimals);
                    })),
                        (unformattedNewValue = item[kpi]?.new?.map((numberItem: number) => {
                            return _.isNil(numberItem) ? numberItem : _.round(numberItem, config[kpi]?.allowedDecimals);
                        })),
                        (originalValue = item[kpi]?.old?.map((numberItem: number) => {
                            return _.isNil(numberItem) ? numberItem : _.round(numberItem, config[kpi]?.allowedDecimals);
                        })),
                        (newValue = item[kpi]?.new?.map((numberItem: number) => {
                            return _.isNil(numberItem) ? numberItem : _.round(numberItem, config[kpi]?.allowedDecimals);
                        }));
                }
                tempObj[config[kpi]?.label] = {
                    unformattedNewValue: unformattedNewValue,
                    unformattedOriginalValue: unformattedOriginalValue,
                    kpiConfigObject: { [kpi]: config[kpi] },
                    newValue: newValue,
                    percentageChangeValue: getPercentageChangeV2(unformattedOriginalValue, unformattedNewValue),
                    absoluteChangeValue: formatNumWithKMBAlt(
                        Number(unformattedNewValue) - Number(unformattedOriginalValue),
                        config[kpi]?.allowedDecimals,
                    ),
                    originalValue: originalValue,
                };
            });
            result.push(tempObj);
        });
        return result;
    } catch (error) {
        throw error;
    }
}

function formatPlaybookNumbers(range: any[], pre: string, post: string, appConfig: any) {
    const updatedRange = range.map((rangeItem) => {
        return (
            pre +
            formatNumWithKMBAlt(
                rangeItem,
                post === '%' ? appConfig?.decimalForPercentage : appConfig?.decimalForNonPercentage,
                post === '%' ? undefined : appConfig?.decimalForNonPercentage,
            ) +
            post
        );
    });
    return updatedRange?.join(' - ');
}

export function planComparisonTableParser(data: any[], kpiConfigData: any, commonConfig: any) {
    try {
        const exportData: any[] = [];
        const exportHeader: any[] = [];
        const result = data?.map((item, dataIndex: number) => {
            let pre = '';
            let post = '';
            let kpiLabel = '';
            let allowedDecimals = 0;
            if (kpiConfigData && item?.KPI in kpiConfigData) {
                pre = kpiConfigData?.[item?.KPI]?.pre;
                post = kpiConfigData?.[item?.KPI]?.post;
                kpiLabel = kpiConfigData?.[item?.KPI]?.planLabel;
                allowedDecimals = kpiConfigData?.[item?.KPI]?.allowedDecimals;
            }
            const plans = Object.keys(item).slice(1); //remove KPI item
            const tempObj: any = {};
            let basePlanVal: any = null;
            const tempExportData: any[] = [];
            plans.forEach((plan: any, index: number) => {
                const unformattedValue1 = item[plan];
                const absChange = _.isNil(basePlanVal) ? undefined : unformattedValue1 - basePlanVal;
                const percentageChangeValue = _.isNil(basePlanVal)
                    ? undefined
                    : getPercentageChangeV2(basePlanVal, unformattedValue1);

                const percentageChangeValueExport = _.isNil(basePlanVal)
                    ? undefined
                    : getPercentageChangeV2Decimal(basePlanVal, unformattedValue1);

                if (index === 0) {
                    basePlanVal = unformattedValue1;
                }
                //export header
                //only add export header once
                if (dataIndex === 0) {
                    if (index === 0) {
                        exportHeader.push('KPI', `${plan}_actual`);
                    } else {
                        exportHeader.push(`${plan}_actual`, `${plan}_diff_%`, `${plan}_diff_abs`);
                    }
                }
                //adding export data
                if (index === 0) {
                    tempExportData.push(kpiLabel, unformattedValue1);
                } else {
                    tempExportData.push(unformattedValue1, percentageChangeValueExport, absChange);
                }

                const value1 = _.isNil(unformattedValue1)
                    ? null
                    : formatNumbers(
                          unformattedValue1,
                          pre,
                          post,
                          allowedDecimals,
                          commonConfig?.decimalForPercentage,
                          true,
                      );
                const value2 =
                    index === 0 || _.isNil(percentageChangeValue)
                        ? null
                        : formatNumbers(
                              percentageChangeValue,
                              '',
                              '%',
                              allowedDecimals,
                              commonConfig?.decimalForPercentage,
                              true,
                          );
                const value3 =
                    index === 0 || _.isNil(absChange)
                        ? null
                        : formatNumbers(
                              absChange,
                              pre,
                              post,
                              allowedDecimals,
                              commonConfig?.decimalForPercentage,
                              true,
                          );

                tempObj[plan] = {
                    value1,
                    value2,
                    value3,
                    color:
                        index === 0
                            ? undefined
                            : percentageChangeValue === 0
                            ? colorConstants.greyAlt14
                            : (percentageChangeValue ?? 0) > 0
                            ? colorConstants.limeGreen
                            : colorConstants.redAlt,
                };
            });
            exportData.push(tempExportData);

            return {
                key: item?.KPI,
                KPI: kpiLabel,
                ...tempObj,
            };
        });
        return { result, exportData, exportHeader };
    } catch (error) {
        throw error;
    }
}
